import * as Logger from './logger.js'
import { fetchEventSource } from '@microsoft/fetch-event-source';
import Keycloak from 'keycloak-js';
import { UserSession } from './session.js';
import RbacPolicy from '../shared/rbac-policy.js';

class AuthSession {
    keycloak = null;
    sseAbortController = null;
    sseReconnectTimer = null;
    sseReconnectDelayMs = 10000;
    authInvalidNotified = false;

    constructor() { }

    logout() {
        this.sseCleanup();
        this.setSseUiState({ phase: 'loading', message: 'signed out' });
        this.keycloak?.logout();
        return false;
    }

    setSseUiState({ phase, message = '' } = {}) {
        const next = {
            phase: phase || 'loading',
            message,
            updatedAt: Date.now(),
        };
        UserSession.set('sseStatus', next);
        window.dispatchEvent(new CustomEvent('hs-sse-status', { detail: next }));
    }

    sseCleanup({ resetSubscriptions = true } = {}) {
        if (this.sseReconnectTimer) {
            window.clearTimeout(this.sseReconnectTimer);
            this.sseReconnectTimer = null;
        }
        if (this.sseAbortController) {
            this.sseAbortController.abort();
            this.sseAbortController = null;
        }
        UserSession.set('sseStreamId', null);
        if (resetSubscriptions) {
            UserSession.set('sseSubscriptions', {});
        }
    }

    getSseHost() {
        return UserSession.get('selected_clouds').length ? UserSession.get('selected_clouds')[0].url : '';
    }

    async buildSseRequestOptions({ payload } = {}) {
        const headers = { 'Cache-Control': 'no-cache', 'Accept': 'text/event-stream' };
        if (payload) { headers['Content-Type'] = 'application/json'; }
        const token = await this.getToken();
        if (token) { headers['Authorization'] = 'Bearer ' + token; }
        return {
            headers,
            body: payload ? JSON.stringify(payload) : undefined,
            fullPath: this.getSseHost() + '/dashboard/sse',
        };
    }

    scheduleSseReconnect({ payload, reason } = {}) {
        if (this.sseReconnectTimer || !this.keycloak || this.authInvalidNotified) {
            return;
        }

        this.setSseUiState({ phase: 'loading', message: `reconnecting: ${reason || 'retrying'}` });

        Logger.warn('Scheduling SSE reconnect', {
            reason,
            retryDelayMs: this.sseReconnectDelayMs,
        });

        this.sseReconnectTimer = window.setTimeout(async () => {
            this.sseReconnectTimer = null;
            try {
                await this.sseConnect({ payload, isReconnect: true });
            } catch (err) {
                Logger.error('SSE reconnect attempt failed: ', err);
                this.scheduleSseReconnect({ payload, reason: err?.message || String(err) });
            }
        }, this.sseReconnectDelayMs);
    }

    async restoreSseSubscriptions() {
        const streamId = UserSession.get('sseStreamId');
        const subscriptions = UserSession.get('sseSubscriptions') || {};
        const entries = Object.entries(subscriptions).filter(([, subscription]) => typeof subscription?.resubscribe === 'function');

        if (!streamId || entries.length < 1) {
            return;
        }

        Logger.info('Restoring SSE subscriptions after reconnect', {
            streamId,
            subscriptionCount: entries.length,
        });

        for (const [ subscriptionId, subscription ] of entries) {
            try {
                await subscription.resubscribe(streamId);
            } catch (err) {
                Logger.error('Failed to restore SSE subscription: ', {
                    subscriptionId,
                    error: err?.message || String(err),
                });
            }
        }
    }

    notifyAuthInvalid({ status, reason } = {}) {
        if (this.authInvalidNotified) {
            return;
        }

        this.authInvalidNotified = true;
        this.sseCleanup({ resetSubscriptions: false });

        Logger.error('SSE authorization failed', {
            status,
            reason,
        });

        this.setSseUiState({ phase: 'alert', message: `SSE authorization failed (${status || 'unknown'}): ${reason || 'authorization failed'}` });

        window.dispatchEvent(new CustomEvent('hs-auth-invalid', {
            detail: {
                status,
                reason: reason || 'authorization failed',
            },
        }));
    }

    async sseConnect({ payload, isReconnect = false } = {}) {
        this.sseCleanup({ resetSubscriptions: false });
        this.sseAbortController = new AbortController();
        const currentController = this.sseAbortController;
        const { headers, body, fullPath } = await this.buildSseRequestOptions({ payload });
        this.setSseUiState({ phase: 'loading', message: isReconnect ? 'reconnecting SSE' : 'connecting SSE' });

        return await new Promise((resolve,reject) => {
            let didResolve = false;

            fetchEventSource(encodeURI(fullPath), {
                openWhenHidden: true, 
                method: 'GET', 
                headers, 
                body,
                signal: currentController.signal,
                onopen: (response) => {
                    if (response.ok) {
                        Logger.info(isReconnect ? 'SSE reconnected' : 'SSE connected');
                    } else {
                        if (response.status === 401 || response.status === 403) {
                            this.notifyAuthInvalid({
                                status: response.status,
                                reason: 'sse unauthorized',
                            });
                        }
                        Logger.error("SSE connect failed: ", response.status, response);
                        this.setSseUiState({ phase: 'alert', message: `SSE connect failed (${response.status})` });
                        if (!didResolve) {
                            reject(new Error(`SSE connect failed with ${response.status}`));
                        }
                        throw new Error(`SSE connect failed with ${response.status}`);
                    }
                },
                onmessage: async (msg) => {
                    if (!msg.event && !msg.data) { Logger.debug("SSE heartbeat"); return; } // ignore keepalives
                    if (msg.event == 'connected') {
                        const obj = JSON.parse(msg.data);
                        Logger.debug("SSE connection id: ", obj.stream_id);
                        UserSession.set('sseStreamId', obj.stream_id);
                        this.setSseUiState({ phase: 'connected', message: '' });
                        if (!didResolve) {
                            didResolve = true;
                            resolve();
                        }
                        if (isReconnect) {
                            await this.restoreSseSubscriptions();
                        }
                        return;
                    }
                    if (msg.event == 'data' || msg.event == 'subscribed') {
                        const obj = JSON.parse(msg.data);
                        const subscriptions = UserSession.get('sseSubscriptions') || {};
                        if (subscriptions[obj.subscription_id]) {
                            if (msg.event == 'data') {
                                subscriptions[obj.subscription_id].onUpdate(obj);
                            } else if (msg.event == 'subscribed') {
                                subscriptions[obj.subscription_id].onSubscribed(obj);
                            }
                        } else {
                            Logger.warn("subscription not found for data update: ", msg);
                        }
                        return;
                    }
                },
                onclose: () => {
                    if (currentController.signal.aborted) {
                        return;
                    }
                    this.sseAbortController = null;
                    UserSession.set('sseStreamId', null);
                    const activeSubs = Object.keys(UserSession.get('sseSubscriptions') || {}).length;
                    if (activeSubs === 0) {
                        Logger.debug('SSE closed by server; no active subscriptions, not reconnecting');
                        this.setSseUiState({ phase: 'idle', message: 'no active live subscriptions' });
                        return;
                    }
                    Logger.warn("SSE closed by server");
                    this.setSseUiState({ phase: 'loading', message: 'SSE closed by server, retrying' });
                    this.scheduleSseReconnect({ payload, reason: 'server-close' });
                    throw new Error('SSE closed by server');
                },
                onerror: (err) => {
                    if (currentController.signal.aborted) {
                        return;
                    }
                    this.sseAbortController = null;
                    UserSession.set('sseStreamId', null);
                    const activeSubs = Object.keys(UserSession.get('sseSubscriptions') || {}).length;
                    if (activeSubs === 0) {
                        Logger.debug('SSE transport error with no active subscriptions; not reconnecting: ', err?.message || String(err));
                        this.setSseUiState({ phase: 'idle', message: 'no active live subscriptions' });
                        return;
                    }
                    Logger.error("SSE error: ", err);
                    this.setSseUiState({ phase: 'loading', message: `SSE transport error: ${err?.message || String(err)}` });
                    this.scheduleSseReconnect({ payload, reason: err?.message || String(err) });
                    throw err || new Error('SSE error');
                }
            }).catch(err => {
                if (currentController.signal.aborted) {
                    return;
                }
                if (!didResolve) {
                    reject(err);
                }
            });
        });
    }

    async fetch({ path, method = 'GET', payload = undefined, expectedStatuses = [] } = {}) {
        const body = payload ? JSON.stringify(payload) : undefined;
        const prefix = '/dashboard';    // TODO: make this come from ENV somehow
        const headers = {
            "Cache-Control": "no-cache",
            "Accept": "application/json"
        };
        if (body) {
            headers["Content-Type"] = "application/json";
        }
        const token = await this.getToken();
        if (token) {
            headers["Authorization"] = "Bearer " + token;
        }
        let host = UserSession.get("selected_clouds").length ? UserSession.get("selected_clouds")[0].url : '';
        let full_path = host + prefix + (path.startsWith('/') ? '' : '/') + path;
        const response = await fetch(full_path, { method, headers, body });
        if (!response.ok) {
            const isExpectedStatus = Array.isArray(expectedStatuses) && expectedStatuses.includes(response.status);
            if (isExpectedStatus) {
                Logger.debug('expected non-OK response', {
                    method,
                    path,
                    status: response.status,
                });
            } else {
                Logger.warn("response: ", response);
            }
            const err = new Error(`fetch ${method} failed with ${response.status}`);
            err.status = response.status;
            err.response = response;
            throw err;
        }
        if (response.status === 204) {
            // no content, return empty array
            return [];
        }
        return await response.json();
    }

    // Build a fully-qualified API URL (host + /dashboard prefix + path) using the
    // same selected-cloud host that fetch() uses. Useful for native browser
    // downloads (<a download>) that need an absolute, header-less URL.
    apiUrl(path) {
        const prefix = '/dashboard';
        const host = UserSession.get('selected_clouds').length ? UserSession.get('selected_clouds')[0].url : '';
        return host + prefix + (path.startsWith('/') ? '' : '/') + path;
    }

    async getToken() {
        // check validity of the current token and, if it's going to expire within 5 seconds, go
        // refresh it and return the new token.  else, return the existing token.
        if (this.keycloak) {
            try {
                const state = await this.keycloak.updateToken(5);
                if (state) { 
                    Logger.debug("Keycloak: refreshed access token..."); 
                }
            } catch(err) {
                Logger.error("Keycloak: token refresh failed: ", err);
                this.keycloak.logout();
            }
            return this.keycloak.token;
        }
        return null;
    }

    base64Decode(base64) {
        try {
            return decodeURIComponent(window.atob(base64).split('').map(function(c) {
                return '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2);
            }).join(''));
        } catch(err) {
            Logger.warn("base64Decode failed: ", err);
            Logger.warn(base64);
        }
    }

    checkLogoutFlag() {
        const params = new URLSearchParams(window.location.search);
        const logout_flag = params.get('logout');
        Logger.verbose("logout_flag: ", logout_flag);
        return logout_flag == 'true';
    }

    async login({ realm, url, idpHint, clientId = 'public-user-client' }) {
        // hook for local development...
        let redirectUri = window.location.origin;
        if (this.checkLogoutFlag()) {
            redirectUri += '?logout=true';
        }
        Logger.debug("redirectUri: " + redirectUri);
        const isLocalDev = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';
        // Disable the 3rd-party cookie iframe check in local dev: modern browsers block it and it
        // causes a 10-second timeout before the Keycloak init fails.  Production is unaffected.
        let initOptions = { redirectUri, ...(isLocalDev && { checkLoginIframe: false }) };
        let opts = { realm, url, clientId, idpHint };
        if (isLocalDev) {
            Logger.info('Keycloak dev login params: ', {
                realm,
                idpHint,
                clientId,
                authUrl: url,
                redirectUri,
            });
        }

        this.keycloak = new Keycloak(opts);
        this.keycloak.onAuthSuccess = () => { Logger.debug('Keycloak: AuthSuccess'); };
        this.keycloak.onAuthError = () => { Logger.debug('Keycloak: AuthError'); };
        this.keycloak.onAuthLogout = () => { Logger.debug('Keycloak: AuthLogout'); };
        this.keycloak.onReady = (state) => { Logger.debug('Keycloak: Ready'); };
        this.keycloak.onAuthRefreshSuccess = () => { Logger.debug('Keycloak: AuthRefreshSuccess'); };
        this.keycloak.onAuthRefreshError = () => { Logger.debug('Keycloak: AuthRefreshError'); };
        this.keycloak.onTokenExpired = () => {
            Logger.debug('Keycloak: TokenExpired'); 
            // NOTE: we can call updateToken(minTimeUntilExpire) anytime we want
            // - the minTimeUntilExpire is seconds, if token is still good for that amount of time, no action is taken
            // - returns true if token was refreshed, false if no refresh was needed, throws error if failed to refresh
            // - call clearToken() anytime to invalidate session and force new login, such as when we get a 401
            this.keycloak.updateToken(30)
                .then(state => { Logger.debug("Refresh token success: ", state); })
                .catch(err => { Logger.error("Refresh token failed: ", err); this.keycloak.logout(); });
        };

        const authenticated = await this.keycloak.init(initOptions);
        if (authenticated) {
            this.authInvalidNotified = false;
            this.setSseUiState({ phase: 'loading', message: 'authenticated, connecting SSE' });
            Logger.debug('Keycloak: Authenticated');

            const logoutFlag = this.checkLogoutFlag();
            Logger.verbose("logout: ", logoutFlag);
            if (logoutFlag) {
                // remove the logout queryString as it effects logout/login redirect flow
                const new_url = new URL(window.location.href)
                new_url.searchParams.delete('logout');
                window.history.replaceState({}, document.title, new_url.toString());
            }

            // parse the token and return it
            try {
                const base64Url = this.keycloak.token.split('.')[1];
                const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
                const jsonPayload = this.base64Decode(base64);
                const userinfo = JSON.parse(jsonPayload);
                if (!userinfo) {
                    Logger.error("Keycloak: Failed to parse token payload: ", jsonPayload);
                    return false;
                }

                const roles = Array.isArray(userinfo.realm_access?.roles)
                    ? Array.from(new Set(userinfo.realm_access.roles.map(role => String(role || '').trim().toLowerCase()).filter(Boolean)))
                    : [];
                const roleSet = new Set(roles);
                userinfo.roles = roles;

                // Usertype/admin/capabilities derivation now delegates to
                // shared/rbac-policy.js so this mirrors the exact semantics
                // of homesight-lib/lib/rbac.js's RBACEngine (server-side)
                // rather than maintaining a hand-duplicated copy here.
                userinfo.capabilities = RbacPolicy.deriveCapabilities(roleSet);

                if (RbacPolicy.deriveAdmin(roleSet)) {
                    userinfo.admin = true;
                }

                userinfo.usertype = RbacPolicy.deriveUsertype(roleSet);
                Logger.info(` -- ${userinfo.usertype.toUpperCase()} user --`);
                // TODO: for staff-admin, set userinfo.admin = true, for regular staff or user leave blank
                UserSession.set('userinfo', userinfo);

                await UserSession.reloadData();
                const clouds = UserSession.get("clouds");
                const orgs = UserSession.get("orgs");
                const providers = UserSession.get("providers");
                const user_providers = UserSession.get("user_providers");

                if (clouds.length > 1) {
                    UserSession.set("filtered_clouds", clouds);
                }
                UserSession.set("filtered_orgs", orgs);
                UserSession.set("filtered_providers", providers);

                const shouldAutoRedirect = userinfo.usertype == 'user' && !roleSet.has('ml') && !roleSet.has('ml-admin');
                if (shouldAutoRedirect) {
                    if (providers.length < 2) {
                        if (user_providers.length == 1) {
                            //
                            // this user is normally auto-redirected to the user portal.  if this was a logout, then
                            // execute a keycloak logout to go back to the login screen
                            //
                            if (logoutFlag) {
                                Logger.verbose("executing logout");
                                this.keycloak.logout();
                                return false;
                            }

                            //
                            // auto-redirect to the user portal for their single provider
                            //
                            const url = 'https://' + new URL(window.location.href).hostname + '?realm=' + user_providers[0].providername + '&org=' + user_providers[0].orgname;
                            if (window.location.href.includes('localhost')) {
                                Logger.info("not redirecting local dev-mode");
                            } else {
                                window.location.href = url;
                                return false;
                            }
                        }
                    }
                }

                // open our SSE connection to the server for live update streaming
                await this.sseConnect();

                return userinfo;
            } catch(err) {
                Logger.debug(err);
            }
        } else {
            Logger.debug('Keycloak: Authenticating...');
            const auth_retval = await this.keycloak.login({ idpHint, redirectUri });
            // user should have been redirected, never normally get here...
            Logger.error("Keycloak: login failed and returned: ", auth_retval);
        }
    }
}
export const Auth = new AuthSession();
